import { forwardRef } from 'react';
import styled from 'styled-components';

import { useIwBreakPoints } from '@/_pages/interwill/hooks/useIwBreakPoints';
import { Spreadsheet } from '@/shared/components/Spreadsheet';
import { files } from '@/shared/utils/files';

import { isSupportedType } from '../helpers';
import { ImageContainer, ImagePage, ImagePreview, ImageSidebar, ImageWrapper } from '../styled';
import { StyledText } from '../ThumbnailItem/styled';
import { FilePreviewData } from '../types';
import { UnsupportedFormat } from '../UnsupportedFormat';

import { DocxPreview } from './DocxPreview';
import { PdfRenderer } from './PdfRenderer';

type FileRendererProps = {
  fileData: FilePreviewData;
  containerWidth: number;
  thumbnailWidth: number;
  headerHidden: boolean;
  setHeaderHidden: (b: boolean) => void;
};

export const FileRenderer = forwardRef<HTMLDivElement, FileRendererProps>(
  ({ fileData, containerWidth, thumbnailWidth, headerHidden, setHeaderHidden }, ref) => {
    const { isMobile } = useIwBreakPoints();

    const isSupported = fileData.mode === 'file' && isSupportedType(fileData.file.type);

    if (fileData.mode === 'file' && !isSupported) {
      return <UnsupportedFormat />;
    }

    if (fileData.mode === 'fileInfo' && fileData.fileInfo.fileType === 'image') {
      return (
        <ImageWrapper>
          {!isMobile && (
            <ImageSidebar>
              <ImageContainer>
                <ImagePreview src={fileData.fileInfo.url} alt={fileData.fileInfo.name} />
                <StyledText>1</StyledText>
              </ImageContainer>
            </ImageSidebar>
          )}
          <ImagePage>
            <img
              src={fileData.fileInfo.url}
              alt={fileData.fileInfo.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          </ImagePage>
        </ImageWrapper>
      );
    }

    if (
      (fileData.mode === 'file' && fileData.file.type === 'application/pdf') ||
      (fileData.mode === 'fileInfo' && fileData.fileInfo.fileType === 'pdf')
    ) {
      return (
        <PdfRenderer
          fileData={fileData}
          thumbnailWidth={thumbnailWidth}
          containerWidth={containerWidth}
          headerHidden={headerHidden}
          setHeaderHidden={setHeaderHidden}
        />
      );
    }

    if (fileData.mode === 'file' && files.getExtension(fileData.file.name) === 'xlsx') {
      return <SpreadsheetWide file={fileData.file} ref={ref} />;
    }

    if (fileData.mode === 'fileInfo' && fileData.fileInfo.fileType === 'xlsx') {
      return <SpreadsheetWide file={fileData.fileInfo.url} ref={ref} />;
    }

    if (
      (fileData.mode === 'fileInfo' && fileData.fileInfo.fileType === 'docx') ||
      (fileData.mode === 'file' && files.getExtension(fileData.file.name) === 'docx')
    ) {
      return <DocxPreview fileData={fileData} ref={ref} />;
    }

    return <UnsupportedFormat />;
  },
);

FileRenderer.displayName = 'FileRenderer';

const SpreadsheetWide = styled(Spreadsheet)`
  width: calc(100% + 96px);

  @media (max-width: 1360px) {
    margin-top: 24px;
    width: 100%;
  }
`;
